import { Box, Stack, Text, useMantineTheme } from '@mantine/core';
import { CartesianGrid, Line, LineChart, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import type { TccData } from '../types';
import { latest, pctChange, previous } from '../helpers';
import { scoreAllTimeCountGoal } from '../../statusUtils';
import { CardShell } from './CardShell';
import { MetricRow } from './MetricRow';

export function GraduatesCard({ data }: { data: TccData }) {
  const theme = useMantineTheme();
  const graduatesSeries = data.graduates;
  const cur = latest(graduatesSeries);
  const prev = previous(graduatesSeries);

  const totalGraduates = data.graduatesAllTimeTotal;
  const totalParticipants = data.participantsAllTimeTotal;
  const completionRate = totalParticipants > 0 ? (totalGraduates / totalParticipants) * 100 : null;
  const scored = scoreAllTimeCountGoal({
    value: totalGraduates,
    goals: data.goalsByMetric['tcc.graduates'],
    field: 'graduates',
    quarterLabel: cur.quarter ?? '',
  });
  const goalAllTime = scored.goal;
  const status = scored;

  const qoq = pctChange(cur.graduates, prev?.graduates ?? null);
  const arrow = (n: number): '↑' | '↓' | '' => (n > 0 ? '↑' : n < 0 ? '↓' : '');
  const qoqText = qoq === null ? '—' : arrow(qoq) ? `${arrow(qoq)}${Math.abs(qoq).toFixed(2)}% QoQ` : '0.00% QoQ';

  const parseQuarter = (label: string): { year: number; quarter: number } | null => {
    const match = label.match(/^Q([1-4])\s+(\d{4})$/);
    if (!match) return null;
    return { year: Number(match[2]), quarter: Number(match[1]) };
  };

  const currentMeta = parseQuarter(cur.quarter);
  const ytd = currentMeta
    ? graduatesSeries
        .filter((point) => {
          const meta = parseQuarter(point.quarter);
          return meta?.year === currentMeta.year && meta.quarter <= currentMeta.quarter;
        })
        .reduce((sum, point) => sum + point.graduates, 0)
    : 0;

  const yoy = (() => {
    if (!currentMeta) return null;
    const prevYearTotal = graduatesSeries
      .filter((point) => {
        const meta = parseQuarter(point.quarter);
        return meta?.year === currentMeta.year - 1 && meta.quarter <= currentMeta.quarter;
      })
      .reduce((sum, point) => sum + point.graduates, 0);
    return pctChange(ytd, prevYearTotal);
  })();
  const yoyText = yoy === null ? '— YoY' : yoy === 0 ? '0.00% YoY' : `${arrow(yoy)}${Math.abs(yoy).toFixed(2)}% YoY`;

  const tooltipShellStyle = {
    backgroundColor: theme.colorScheme === 'dark' ? '#000' : '#fff',
    border: `1px solid ${theme.colorScheme === 'dark' ? '#333' : '#ddd'}`,
    borderRadius: 10,
    color: theme.colorScheme === 'dark' ? '#fff' : '#000',
    padding: '10px 12px',
    maxWidth: 320,
    whiteSpace: 'normal' as const,
    wordBreak: 'break-word' as const,
  } as const;

  const seriesTotal = graduatesSeries.reduce((sum, point) => sum + point.graduates, 0);
  const priorGraduates = Math.max(0, totalGraduates - seriesTotal);
  let cumulative = priorGraduates;
  const chartData = graduatesSeries.map((point) => {
    cumulative += point.graduates;
    return {
      ...point,
      cumulative,
    };
  });
  const maxCumulative = Math.max(totalGraduates, ...chartData.map((p) => p.cumulative));

  return (
    <CardShell
      title="Individuals Graduated"
      subtitle={cur.quarter}
      statusLabel={status.label}
      statusColor={status.color}
      statusTooltip={status.tooltip}
    >
      <Stack spacing="sm">
        <MetricRow
          items={[
            {
              label: 'This Quarter',
              labelTooltip: 'Unique individuals who graduated this quarter.',
              value: (
                <Text size="lg" weight={800}>
                  {cur.graduates}
                </Text>
              ),
              sublabel: qoqText,
            },
            {
              label: 'YTD',
              labelTooltip: 'Calendar year to date graduates (sum of quarters in the current year through this quarter).',
              value: (
                <Text size="lg" weight={800}>
                  {ytd}
                </Text>
              ),
              sublabel: yoyText,
            },
            {
              label: 'Graduates (All-Time)',
              labelTooltip:
                goalAllTime != null
                  ? `Total graduates across all time (includes periods not shown). Goal: ${goalAllTime} learners.`
                  : 'Total graduates across all time (includes periods not shown).',
              value: (
                <Text size="lg" weight={800}>
                  {totalGraduates}
                </Text>
              ),
              sublabel: goalAllTime != null ? `Goal ${goalAllTime}` : undefined,
            },
            {
              label: 'Completion Rate (All-Time)',
              labelTooltip: 'Percent of participants who graduated (all-time).',
              value: (
                <Text size="lg" weight={800}>
                  {completionRate == null ? '—' : `${Math.round(completionRate)}%`}
                </Text>
              ),
            },
          ]}
        />

        <Text size="xs" color="dimmed" weight={700} sx={{ letterSpacing: 0.2, marginTop: '0.75rem' }}>
          Cumulative Graduates
        </Text>

        <Box sx={{ height: 170 }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" opacity={0.18} vertical={false} />
              <XAxis dataKey="quarter" tick={{ fontSize: 11 }} axisLine={false} tickLine={false} interval="preserveStartEnd" minTickGap={12} />
              <YAxis
                orientation="right"
                tick={{ fontSize: 11 }}
                allowDecimals={false}
                axisLine={false}
                tickLine={false}
                width={44}
                tickMargin={8}
                domain={[0, maxCumulative]}
              />
              {goalAllTime != null ? (
                <ReferenceLine y={goalAllTime} stroke="#fa5252" strokeDasharray="4 4" ifOverflow="extendDomain" />
              ) : null}
              <Tooltip
                content={({ active, payload, label }) => {
                  if (!active || !payload || payload.length === 0) return null;
                  const p: any = payload[0]?.payload;
                  const v = p?.cumulative;
                  const quarterValue = p?.graduates;
                  return (
                    <div style={tooltipShellStyle as any}>
                      <div style={{ fontWeight: 700, marginBottom: 6, fontSize: 12, opacity: 0.9 }}>{label}</div>
                      <div style={{ fontSize: 12, lineHeight: 1.35 }}>
                        <div style={{ fontWeight: 900, fontSize: 14 }}>{typeof v === 'number' ? v : '—'} Total</div>
                        <div style={{ opacity: 0.9 }}>
                          {typeof quarterValue === 'number' ? quarterValue : '—'} graduated this quarter
                        </div>
                      </div>
                    </div>
                  );
                }}
              />
              <Line
                type="monotone"
                dataKey="cumulative"
                name="Cumulative Graduates"
                stroke={theme.colors.blue[6]}
                strokeWidth={2}
                dot={(dotProps: any) => {
                  const pointKey = dotProps?.payload?.quarter ?? dotProps?.index ?? `${dotProps?.cx}-${dotProps?.cy}`;
                  return (
                    <circle
                      key={`tcc-graduates-dot-${pointKey}`}
                      cx={dotProps.cx}
                      cy={dotProps.cy}
                      r={2}
                      fill={theme.colors.blue[6]}
                      stroke={theme.colorScheme === 'dark' ? 'rgba(0, 0, 0, 0.8)' : 'rgba(255, 255, 255, 0.8)'}
                      strokeWidth={1.5}
                    />
                  );
                }}
                activeDot={{ r: 4 }}
                isAnimationActive={false}
              />
            </LineChart>
          </ResponsiveContainer>
        </Box>
      </Stack>
    </CardShell>
  );
}
